/**
 * `Tooltip` — tiny, accessible info trigger for the Experiments view
 * (ADR-0233 §UI). An `Info` icon rendered as a `<button>` so it is
 * natively focusable, tappable on touch, and keyboard-reachable.
 *
 * Behaviour:
 *   - Hover + focus → opens the popover.
 *   - Tap/click     → toggles it (so a mobile user can read without a
 *                     hover gesture).
 *   - Escape        → closes it.
 *
 * The popover carries `role="tooltip"` and the trigger wires
 * `aria-describedby` to its id while open. Copy is read-only; tooltips
 * never carry interactive content.
 */

import { Info } from 'lucide-react';
import { useId, useState } from 'react';
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export interface TooltipProps {
  /** Short, plain explanation. Keep to ≤ 20 words. */
  readonly label: string;
  /** Icon size in px — defaults to 12. */
  readonly size?: number;
  /** Optional stable id for tests ("tt-signup-rate"). */
  readonly testId?: string;
  /** Where the popover anchors horizontally relative to the icon. */
  readonly align?: 'start' | 'end';
}

export function Tooltip({
  label,
  size = 12,
  testId,
  align = 'start',
}: TooltipProps): JSX.Element {
  const C = useC();
  const id = useId();
  const [open, setOpen] = useState(false);

  const onToggle = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setOpen((v) => !v);
  };

  const onKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const triggerStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: C.muted,
    border: 'none',
    padding: 2,
    marginLeft: 4,
    cursor: 'help',
    borderRadius: 2,
    verticalAlign: 'middle',
  };

  const popStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    [align === 'end' ? 'right' : 'left']: 0,
    marginTop: 6,
    width: 240,
    maxWidth: '70vw',
    padding: '6px 8px',
    fontFamily: sansFont,
    fontSize: 11.5,
    lineHeight: 1.4,
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: 'none',
    textAlign: 'left',
    color: C.body,
    background: C.surface3,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    boxShadow: 'none',
    whiteSpace: 'normal',
    pointerEvents: 'none',
    zIndex: 50,
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={onToggle}
        onKeyDown={onKey}
        aria-describedby={open ? id : undefined}
        aria-label={label}
        data-testid={testId ?? 'tooltip-trigger'}
        style={triggerStyle}
      >
        <Info size={size} aria-hidden="true" />
      </button>
      {open ? (
        <span id={id} role="tooltip" data-testid={testId ? `${testId}-content` : 'tooltip-content'} style={popStyle}>
          {label}
        </span>
      ) : null}
    </span>
  );
}

/**
 * `TooltipText` — a plain text run that itself exposes a tooltip, for
 * table header cells and inline labels where adding a trailing info
 * icon would be noisy. The element is a `<span>` with `tabIndex=0` so
 * keyboard users can reach it, and `aria-describedby` is wired to the
 * popover id while open.
 */
export function TooltipText({
  label,
  children,
  testId,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly testId?: string;
}): JSX.Element {
  const C = useC();
  const id = useId();
  const [open, setOpen] = useState(false);

  const onKey = (e: KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === 'Escape' && open) setOpen(false);
  };

  const popStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 6,
    width: 240,
    maxWidth: '70vw',
    padding: '6px 8px',
    fontFamily: sansFont,
    fontSize: 11.5,
    lineHeight: 1.4,
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: 'none',
    textAlign: 'left',
    color: C.body,
    background: C.surface3,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    whiteSpace: 'normal',
    pointerEvents: 'none',
    zIndex: 50,
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        tabIndex={0}
        role="button"
        aria-describedby={open ? id : undefined}
        aria-label={label}
        data-testid={testId}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKey}
        style={{ cursor: 'help', borderBottom: `1px dotted ${C.dim}` }}
      >
        {children}
      </span>
      {open ? (
        <span
          id={id}
          role="tooltip"
          data-testid={testId ? `${testId}-content` : undefined}
          style={popStyle}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
}
